// ────────────────────────────────────────────────
// src/Pet/Pet.js – single source‑of‑truth for pet maths
// egg → baby → teen → adult
// ────────────────────────────────────────────────
// Pure functions only — call them from UI / Cloud Functions then
// write the returned object back to Firestore.
// ------------------------------------------------------------------

/* ═════════════════════ 1. Global tunables ═════════════════════ */
export const BASE_EXP       = 10;          // EXP for Lv‑1 → 2
export const EXP_FACTOR     = 1.35;        // exponential curve per level
export const HATCH_DELAY    = 5 * 60_000;  // egg countdown: 5 min
export const HUNGER_DECAY   = 1 / 3_600_000; // 1 pt / h
export const THIRST_DECAY   = 1 / 2_700_000; // 1 pt / 45 min

/* stage breakpoints */
const TEEN_LV  = 10;
const ADULT_LV = 25;

/* ═════════════════════ 2. Element balance tables ═════════════════════ */
// six colour / element lines (balanced but different growth focus)
export const ELEMENTS = ["water","earth","wind","nature","fire","light"];

const BASE_COMMON  = { hp:30, mp:10, atk:4, matk:4, def:2, mdef:2, eva:1, cri:1 };
const GROW_COMMON  = { hp:10, mp:4, atk:2, matk:2, def:1, mdef:1, eva:0.3, cri:0.3 };

// per‑element modifiers (additive, can be ±)
const ELEMENT_MODS = {
  water : { hp:+4,  mp:+2,              eva:+0.2                        }, // Blue (tankish, accurate)
  earth : { hp:+8,  def:+1, mdef:+1,    eva:-0.2, cri:-0.1             }, // Brown (defender)
  wind  : { eva:+0.5, cri:+0.3,         hp:-4, def:-1                  }, // Green (evasive critter)
  nature: { mp:+3, matk:+1,             hp:-2, atk:-1, def:-1          }, // Orange (mage‑leaning)
  fire  : { atk:+2, matk:+2, cri:+0.2,  hp:-6, def:-1, mdef:-1         }, // Red (glass cannon)
  light : { atk:+1, matk:+1, def:+1, mdef:+1, hp:+2, eva:-0.1, mp:-2   }, // Gold (balanced)
};

/* tiny helper */
const merge = (a,b) => Object.fromEntries(
  Object.keys({...a,...b}).map(k => [k, (a[k]||0)+(b[k]||0)])
);

export const baseStatsFor   = (el) => merge(BASE_COMMON , ELEMENT_MODS[el] || {});
export const growthFor      = (el) => merge(GROW_COMMON, ELEMENT_MODS[el] || {});

/* ═════════════════════ 3. Utility helpers ═════════════════════ */
export const expNeeded = (lv) => Math.round(BASE_EXP * EXP_FACTOR ** (lv-1));

export const applyGrowth = (element, lv) => {
  const base   = baseStatsFor(element);
  const grow   = growthFor(element);
  const out    = {};
  for (const k in base) out[k] = Math.round(base[k] + grow[k]*(lv-1));
  return out;
};

const stageForLv = (lv) => lv>=ADULT_LV ? "adult" : lv>=TEEN_LV ? "teen" : "baby";

/* ═════════════════════ 4. Image helper ═════════════════════ */
const COLOR = { water:"Blue", fire:"Red", earth:"Brown", wind:"Green", nature:"Orange", light:"Gold" };
const stageImg = (element, stage) =>
  `/images/Pet/${stage}/${COLOR[element] || "Blue"}${stage[0].toUpperCase()+stage.slice(1)}.png`;

/* ═════════════════════ 5. Factory ═════════════════════ */
export const createEgg = (element="water") => {
  const now = Date.now();
  return {
    stage:      "egg",
    element,
    hatchAt:    null,              // set after user taps "Hatch"
    level:      1,
    exp:        0,
    hunger:     100,
    thirst:     100,
    // we keep battle stats off eggs to avoid confusion
    hp:0, mp:0, atk:0, matk:0, def:0, mdef:0, eva:0, cri:0,
    lastUpdate: now,
  };  
};

/* ═════════════════════ 6. Tick (decay + auto‑hatch) ═════════════════════ */
export const tickPet = (pet) => {
  const now = Date.now();
  let p = { ...pet };

  // ① hatch if countdown reached
  if (p.stage === "egg" && p.hatchAt && now >= p.hatchAt) {
    p.stage    = "baby";
    p.photoUrl = stageImg(p.element, "baby");
    // initialise battle stats on hatch
    Object.assign(p, applyGrowth(p.element, 1));
  }

  // ② decay hunger / thirst after hatch
  if (p.stage !== "egg") {
    const dt = now - (p.lastUpdate || now);
    p.hunger = Math.max(0, p.hunger - dt * HUNGER_DECAY);
    p.thirst = Math.max(0, p.thirst - dt * THIRST_DECAY);
  }

  p.lastUpdate = now;
  return p;
};

/* ═════════════════════ 7. EXP / Level helper ═════════════════════ */
const gainExp = (pet, expGain) => {
  let { exp, level } = pet;
  exp += expGain;
  while (exp >= expNeeded(level)) { exp -= expNeeded(level); level++; }

  const newStage = stageForLv(level);
  const photoUrl = newStage !== pet.stage ? stageImg(pet.element,newStage) : pet.photoUrl;

  return { exp, level, stage:newStage, photoUrl, ...applyGrowth(pet.element,level) };
};

/* ═════════════════════ 8. Actions ═════════════════════ */
export const feedPet = (pet, cfg={ hunger:40, exp:3 }) => {
  if (pet.stage === "egg") return pet;
  const hunger = Math.min(100, pet.hunger + cfg.hunger);
  return { ...pet, hunger, ...gainExp(pet, cfg.exp) };
};

export const drinkPet = (pet, cfg={ thirst:40, exp:2 }) => {
  if (pet.stage === "egg") return pet;
  const thirst = Math.min(100, pet.thirst + cfg.thirst);
  return { ...pet, thirst, ...gainExp(pet, cfg.exp) };
};

/* ═════════════════════ 9. Evolution helper (teen) ═════════════════════ */
export const evolvePet = (pet) => {
  if (pet.stage !== "baby" || pet.level < 20) return pet;  // only baby ≥20 lv
  const stage = "teen";
  const photoUrl = stageImg(pet.element, stage);
  return { ...pet, stage, photoUrl, ...applyGrowth(pet.element, pet.level) };
};
